import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { FormControl, InputLabel, Select, MenuItem, Grid } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import SubsegmentModal from "../../components/SubsegmentModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filtersRow: {
    marginBottom: theme.spacing(2),
  }
}));

const normalize = value => (value || "").toString().toLowerCase();

const Subsegments = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [segmentFilter, setSegmentFilter] = useState("");
  const [segments, setSegments] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadSegments = async () => {
    try {
      const { data } = await api.get("/segments");
      setSegments((Array.isArray(data?.segments) ? data.segments : []).filter(item => item && typeof item === "object" && item.id != null));
    } catch (err) {
      toastError(err);
      setSegments([]);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/subsegments", {
        params: { pageNumber: 1 }
      });
      setRecords((Array.isArray(data?.subsegments) ? data.subsegments : []).filter(item => item && typeof item === "object" && item.id != null));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
      setRecords([]);
    }
  };

  useEffect(() => {
    loadSegments();
    fetchRecords();
  }, []);

  const visibleRecords = useMemo(() => {
    const q = normalize(searchParam);

    return records.filter(record => {
      const matchesSearch =
        !q ||
        normalize(record?.name).includes(q) ||
        normalize(record?.segment?.name).includes(q);

      const matchesSegment = segmentFilter
        ? Number(record?.segmentId) === Number(segmentFilter)
        : true;

      return matchesSearch && matchesSegment;
    });
  }, [records, searchParam, segmentFilter]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/subsegments/${id}`);
      toast.success("Subsegmento removido com sucesso");
      setRecords(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      toastError(err);
    }
    setConfirmOpen(false);
    setDeletingRecord(null);
  };

  return (
    <MainContainer>
      <SubsegmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        subsegmentId={selectedRecord?.id}
        onSave={fetchRecords}
      />

      <ConfirmationModal
        title={deletingRecord ? `Excluir ${deletingRecord.name}?` : "Confirmar exclusão"}
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeletingRecord(null);
        }}
        onConfirm={() => handleDelete(deletingRecord.id)}
      >
        Esta ação não pode ser revertida.
      </ConfirmationModal>

      <MainHeader>
        <Title>Subsegmentos</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Pesquisar"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" color="primary" onClick={() => {
            setSelectedRecord(null);
            setModalOpen(true);
          }}>
            Novo Subsegmento
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={2} className={classes.filtersRow}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por Segmento</InputLabel>
            <Select value={segmentFilter} onChange={(e) => setSegmentFilter(e.target.value)}>
              <MenuItem value=""><em>Todos</em></MenuItem>
              {segments.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRecords.filter(item => item && typeof item === "object" && item.id != null).map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record?.name || ""}</TableCell>
                <TableCell>{record?.segment?.name || ""}</TableCell>
                <TableCell>{record?.active ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => {
                    setSelectedRecord(record);
                    setModalOpen(true);
                  }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    setDeletingRecord(record);
                    setConfirmOpen(true);
                  }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <TableRowSkeleton key={i} columns={4} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Subsegments;
